import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, Trash2 } from "react-feather";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import { useMasterPermissions } from "../useMasterPermissions";
import {
  useGetDeductionsQuery,
  useGetDeductionQuery,
  useCreateDeductionMutation,
  useUpdateDeductionMutation,
  useDeleteDeductionMutation,
  useReplaceDeductionDetailsMutation,
} from "./deductionsApi";

const CONDITIONS = ["Below", "Between", "Above"];
const emptyBuilder = { condition: "", fromAmount: "0", toAmount: "0", amount: "", isPercentage: false };

// Mirrors GUI/Master/Frm_Deductions.cs, now against the live
// /api/v1/masters/deductions endpoints (handoff §9.4). Slabs use the
// full-list diff pattern: send the whole desired set, each row optionally
// carrying recordGuid (null = new, present = update, omitted = delete).
// Reading requires Deductions.View (route-gated); the deduction master
// row's own create requires Deductions.Create, but everything else -
// updating the name, the slab rows (a sub-resource write), and delete -
// is Deductions.Edit (§13.3's "only the top-level create action gets
// .Create" rule), so saving a brand new deduction with slabs needs BOTH
// permissions even though the UI does it in one Save click.
const DeductionsPage = () => {
  const { canCreate, canEdit } = useMasterPermissions("Deductions");
  const { data: deductions = [] } = useGetDeductionsQuery();
  const [createDeduction] = useCreateDeductionMutation();
  const [updateDeduction] = useUpdateDeductionMutation();
  const [deleteDeduction] = useDeleteDeductionMutation();
  const [replaceDetails, { isLoading: isSaving }] = useReplaceDeductionDetailsMutation();

  const [mode, setMode] = useState("new"); // "new" | "view" | "edit"
  const [selectedId, setSelectedId] = useState(null);
  const { data: detail, isFetching } = useGetDeductionQuery(selectedId, { skip: !selectedId });

  const [name, setName] = useState("");
  const [bands, setBands] = useState([]);
  const [builder, setBuilder] = useState(emptyBuilder);
  const [editingBandIndex, setEditingBandIndex] = useState(null);

  const isReadOnly = mode === "view";
  const canWriteMaster = mode === "new" ? canCreate : canEdit;

  useEffect(() => {
    if (mode === "new") {
      setName("");
      setBands([]);
    } else if (detail) {
      setName(detail.deduction?.deductionName || "");
      setBands(detail.details || []);
    }
  }, [mode, detail]);

  const resetBuilder = () => {
    setBuilder(emptyBuilder);
    setEditingBandIndex(null);
  };

  const startNew = () => {
    setSelectedId(null);
    setMode("new");
    resetBuilder();
  };

  const selectDeduction = (id) => {
    setSelectedId(Number(id));
    setMode("view");
    resetBuilder();
  };

  const startEdit = () => setMode("edit");

  const handleConditionChange = (condition) => {
    setBuilder((prev) => ({
      ...prev,
      condition,
      fromAmount: condition === "Below" ? "0" : prev.fromAmount === "0" ? "" : prev.fromAmount,
      toAmount: condition === "Above" ? "0" : prev.toAmount === "0" ? "" : prev.toAmount,
    }));
  };

  const addOrUpdateBand = () => {
    if (!builder.condition) {
      toast.error("Select a condition");
      return;
    }
    if (builder.amount === "" || Number.isNaN(Number(builder.amount))) {
      toast.error("Invalid amount format");
      return;
    }
    const band = {
      recordGuid: editingBandIndex != null ? bands[editingBandIndex]?.recordGuid ?? null : null,
      condition: builder.condition,
      fromAmount: Number(builder.fromAmount) || 0,
      toAmount: Number(builder.toAmount) || 0,
      amount: Number(builder.amount),
      isPercentage: builder.isPercentage,
    };
    setBands((prev) => {
      const next =
        editingBandIndex != null
          ? prev.map((b, i) => (i === editingBandIndex ? band : b))
          : [...prev, band];
      return next.map((b, i) => ({ ...b, slno: i + 1 }));
    });
    resetBuilder();
  };

  const selectBandRow = (index) => {
    const band = bands[index];
    setBuilder({
      condition: band.condition,
      fromAmount: String(band.fromAmount ?? 0),
      toAmount: String(band.toAmount ?? 0),
      amount: String(band.amount ?? ""),
      isPercentage: Boolean(band.isPercentage),
    });
    setEditingBandIndex(index);
  };

  const removeBand = (index) => {
    setBands((prev) => prev.filter((_, i) => i !== index).map((b, i) => ({ ...b, slno: i + 1 })));
    if (editingBandIndex === index) resetBuilder();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (bands.length === 0) {
      toast.error("Add at least one band");
      return;
    }
    try {
      let id = selectedId;
      if (mode === "edit" && selectedId != null) {
        await updateDeduction({ id: selectedId, deductionName: name.trim() }).unwrap();
      } else {
        const created = await createDeduction({ deductionName: name.trim() }).unwrap();
        id = created.deduction.deductionId;
      }
      await replaceDetails({
        id,
        details: bands.map((b, i) => ({
          recordGuid: b.recordGuid ?? null,
          slno: i + 1,
          condition: b.condition,
          fromAmount: b.fromAmount,
          toAmount: b.toAmount,
          amount: b.amount,
          isPercentage: b.isPercentage,
        })),
      }).unwrap();
      toast.success("Deduction saved");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save deduction");
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    const confirmed = await confirmDelete({ title: `Delete ${name}?` });
    if (!confirmed) return;
    try {
      await deleteDeduction(selectedId).unwrap();
      toast.success("Deduction deleted");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not delete deduction");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Deductions</h4>
              <h6>Condition-based deduction bands (e.g. NHIF, NSSF)</h6>
            </div>
          </div>
        </div>

        {!canCreate && !canEdit && (
          <div className="alert alert-info">
            You have read-only access. Managing deductions requires Deductions.Create/.Edit and
            an all-branch administrator.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Deduction details</h5>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Ded Id</label>
                {deductions.length > 0 ? (
                  <select
                    className="form-control"
                    value={mode === "new" ? "" : selectedId ?? ""}
                    onChange={(e) => (e.target.value ? selectDeduction(e.target.value) : startNew())}
                    disabled={mode === "edit"}
                  >
                    <option value="">-- New --</option>
                    {deductions.map((d) => (
                      <option key={d.deductionId} value={d.deductionId}>
                        {d.deductionName} ({d.deductionId})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input type="text" className="form-control" value="Auto-assigned" readOnly disabled />
                )}
              </div>
              <div className="col-md-9 mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  readOnly={isReadOnly || !canWriteMaster}
                  disabled={isReadOnly || !canWriteMaster}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Condition</label>
                <select
                  className="form-control"
                  value={builder.condition}
                  onChange={(e) => handleConditionChange(e.target.value)}
                  disabled={isReadOnly || !canEdit}
                >
                  <option value="">--Select--</option>
                  {CONDITIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">From Amount</label>
                <input
                  type="number"
                  className="form-control"
                  value={builder.fromAmount}
                  onChange={(e) => setBuilder((prev) => ({ ...prev, fromAmount: e.target.value }))}
                  disabled={isReadOnly || !canEdit || builder.condition === "Below"}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">To Amount</label>
                <input
                  type="number"
                  className="form-control"
                  value={builder.toAmount}
                  onChange={(e) => setBuilder((prev) => ({ ...prev, toAmount: e.target.value }))}
                  disabled={isReadOnly || !canEdit || builder.condition === "Above"}
                />
              </div>
              <div className="col-md-2 mb-3">
                <label className="form-label">Amount</label>
                <input
                  type="text"
                  className="form-control"
                  value={builder.amount}
                  onChange={(e) => setBuilder((prev) => ({ ...prev, amount: e.target.value }))}
                  disabled={isReadOnly || !canEdit}
                />
              </div>
              <div className="col-md-1 mb-3 d-flex flex-column justify-content-end">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isPercentage"
                    checked={builder.isPercentage}
                    onChange={(e) => setBuilder((prev) => ({ ...prev, isPercentage: e.target.checked }))}
                    disabled={isReadOnly || !canEdit}
                  />
                  <label className="form-check-label" htmlFor="isPercentage">Is %</label>
                </div>
              </div>
              {canEdit && (
                <div className="col-12 mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={addOrUpdateBand}
                    disabled={isReadOnly}
                  >
                    <Plus size={14} className="me-1" />
                    {editingBandIndex != null ? "Update Band" : "Add Band"}
                  </button>
                </div>
              )}

              <div className="col-12">
                <div className="table-responsive mb-3">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Condition</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Amount</th>
                        {!isReadOnly && canEdit && <th style={{ width: 40 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {isFetching && mode !== "new" ? (
                        <tr><td colSpan={5}>Loading...</td></tr>
                      ) : (
                        bands.map((band, index) => (
                          <tr
                            key={band.recordGuid || index}
                            style={{ cursor: !isReadOnly && canEdit ? "pointer" : "default" }}
                            onClick={() => !isReadOnly && canEdit && selectBandRow(index)}
                          >
                            <td>{band.condition}</td>
                            <td>{band.fromAmount}</td>
                            <td>{band.toAmount}</td>
                            <td>{band.amount}{band.isPercentage ? "%" : ""}</td>
                            {!isReadOnly && canEdit && (
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeBand(index);
                                  }}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                      {!isFetching && bands.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-muted">No bands added yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {(canCreate || canEdit) && (
              <div className="d-flex gap-2">
                {mode !== "view" && canWriteMaster && (
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    Save
                  </button>
                )}
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-primary" onClick={startEdit}>
                    Edit
                  </button>
                )}
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
                    Delete
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={startNew}>
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeductionsPage;
